<template>
  <Layout>
    <div class="abstract-details-container">
      <div class="container">
        <!-- 返回按钮 -->
        <div class="back-button-section mb-4">
          <button class="btn btn-outline-secondary" @click="goBack">
            <i class="fa fa-arrow-left"></i> Back to Abstract Overview
          </button>
        </div>

        <!-- 摘要详情卡片 -->
        <div class="card abstract-details-card" v-if="abstract">
          <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
              <h2 class="card-title mb-0">Abstract Details</h2>
                             <div class="abstract-status">
                 <span class="badge" :class="getStatusBadgeClass(abstract.status)">
                   {{ getStatusText(abstract.status) }}
                 </span>
               </div>
            </div>
          </div>
          
          <div class="card-body">
            <!-- 基本信息 -->
            <div class="section mb-4">
              <h4 class="section-title">Basic Information</h4>
              <div class="row">
                <div class="col-md-8">
                  <div class="info-item">
                    <label class="info-label">Abstract Code:</label>
                    <span class="info-value">{{ abstract.ab_no }}</span>
                  </div>
                  <div class="info-item">
                    <label class="info-label">Title:</label>
                    <span class="info-value title-text">{{ abstract.title }}</span>
                  </div>
                  <div class="info-item">
                    <label class="info-label">Presentation Type:</label>
                    <span class="info-value">
                      <span class="badge badge-info">{{ abstract.mode }}</span>
                    </span>
                  </div>
                  <div class="info-item">
                    <label class="info-label">Session:</label>
                    <span class="info-value">{{ abstract.session.session_no }} - {{ abstract.session.title }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="info-item">
                    <label class="info-label">Submitted Date:</label>
                    <span class="info-value">{{ formatDate(abstract.createtime) }}</span>
                  </div>
                  <div class="info-item">
                    <label class="info-label">Review Status:</label>
                    <span class="info-value">{{ getStatusText(abstract.status) }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- 拒绝原因 -->
            <div class="section mb-4" v-if="abstract.status === 2">
              <h4 class="section-title">Rejection Reason</h4>
              <div class="rejection-reason">
                <p>{{ abstract.reject }}</p>
              </div>
            </div>

            <!-- 摘要内容 -->
            <div class="section mb-4">
              <h4 class="section-title">Abstract Content</h4>
              <div class="abstract-content">
                <!-- 自动渲染 KaTeX: 先渲染 ql-formula，再对文中 \( ... \) 或 $$...$$ 做二次渲染 -->
                <div ref="content" v-html="abstract.content"></div>
              </div>
            </div>

            <!-- 作者信息 -->
            <div class="section mb-4">
              <h4 class="section-title">Authors</h4>
              <div class="authors-list">
                <div 
                  v-for="(author, index) in getAuthorsList(abstract.authors_new)" 
                  :key="index"
                  class="author-item"
                >
                  <div class="author-info">
                    <span class="author-name">{{ author.trim() }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 操作按钮 -->
            <div class="section">
              <h4 class="section-title">Actions</h4>
              <div class="action-buttons">
                                 <button 
                   class="btn btn-outline-secondary btn-sm mr-2" 
                   @click="editAbstract"
                   v-if="abstract.status === 0"
                 >
                   <i class="fa fa-edit"></i> Edit Abstract
                 </button>
                 <button 
                   class="btn btn-outline-danger btn-sm mr-2" 
                   @click="deleteAbstract"
                   v-if="abstract.status === 0"
                 >
                   <i class="fa fa-trash"></i> Delete Abstract
                 </button>
                <button class="btn btn-outline-primary btn-sm" @click="downloadAbstract">
                  <i class="fa fa-download"></i> Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 加载状态 -->
        <div v-else-if="loading" class="text-center">
          <div class="spinner-border" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2">Loading abstract details...</p>
        </div>

        <!-- 错误状态 -->
        <div v-else class="text-center">
          <div class="alert alert-danger">
            <h4>Abstract Not Found</h4>
            <p>The abstract you're looking for could not be found.</p>
            <button class="btn btn-primary" @click="goBack">Go Back</button>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import { apiService } from '@/config/api'
import katex from 'katex'
import renderMathInElement from 'katex/contrib/auto-render/auto-render'

export default {
  name: 'AbstractDetails',
  components: {
    Layout
  },
  data() {
    return {
      abstract: null,
      loading: true
    }
  },
  created() {
    // 优先从sessionStorage读取摘要详情
    const cache = sessionStorage.getItem('abstract_detail')
    if (cache) {
      this.abstract = JSON.parse(cache)
      this.loading = false
      sessionStorage.removeItem('abstract_detail')
    } else {
      this.fetchAbstractDetails()
    }
  },
  mounted() {
    this.$nextTick(this.renderFormulas)
  },
  updated() {
    this.$nextTick(this.renderFormulas)
  },
  methods: {
    renderFormulas() {
      try {
        const nodes = this.$el.querySelectorAll('span.ql-formula')
        nodes.forEach(el => {
          const value = el.getAttribute('data-value') || ''
          try { katex.render(value, el, { throwOnError: false }) } catch (e) {}
        })
        // 二次渲染：处理直接粘贴的 LaTeX 文本（如 \(...\)、\[...\]、$$...$$）
        const container = this.$refs.content
        if (container) {
          try {
            renderMathInElement(container, {
              delimiters: [
                {left: '$$', right: '$$', display: true},
                {left: '\\[', right: '\\]', display: true},
                {left: '\\(', right: '\\)', display: false}
              ],
              throwOnError: false
            })
          } catch (e) {}
        }
      } catch (e) {}
    },
    async fetchAbstractDetails() {
      const abstractId = this.$route.params.id
      if (!abstractId) {
        this.loading = false
        return
      }

      try {
        // 这里应该调用实际的API获取摘要详情
        // const response = await apiService.abstracts.getDetails(abstractId)
        
        // 模拟数据 - 实际使用时替换为API调用
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // 模拟摘要数据
        this.abstract = {
          // id: abstractId,
          // title: 'Climate Change Impact on Asian Monsoon Systems',
          // content: 'This study examines the long-term trends in Asian monsoon patterns and their correlation with global climate change indicators. Through comprehensive analysis of meteorological data spanning over three decades, we identify significant shifts in precipitation patterns, temperature variations, and atmospheric circulation patterns across the Asian continent. Our findings suggest that anthropogenic climate change has contributed to increased variability in monsoon intensity and timing, with implications for agricultural productivity, water resource management, and disaster preparedness in affected regions. The research employs advanced statistical modeling techniques and satellite-derived climate data to provide robust evidence of these changes and their potential future trajectories.',
          // mode: 'oral',
          //            status: 1,
          // authors_new: [
          //   {
          //     user_id: '1',
          //     email: 'li.wei@example.com',
          //     first_name: 'Li',
          //     last_name: 'Wei',
          //     title: 'Dr',
          //     country: 'China',
          //     job_title: 'Research Scientist'
          //   },
          //   {
          //     user_id: '2',
          //     email: 'sarah.johnson@example.com',
          //     first_name: 'Sarah',
          //     last_name: 'Johnson',
          //     title: 'Dr',
          //     country: 'USA',
          //     job_title: 'Professor'
          //   },
          //   {
          //     user_id: '3',
          //     email: 'chen.ming@example.com',
          //     first_name: 'Chen',
          //     last_name: 'Ming',
          //     title: 'Prof',
          //     country: 'China',
          //     job_title: 'Professor'
          //   }
          // ],
          // submittedDate: 1736966400000, // 2025-01-15 的时间戳
          // session: 'Atmospheric Sciences Session A',
          // reviewStatus: 'Under Review'
        }
      } catch (error) {
        console.error('Failed to fetch abstract details:', error)
      } finally {
        this.loading = false
      }
    },

    formatDate(dateValue) {
      if (!dateValue) return 'N/A'
      
      // 如果是时间戳（数字）
      if (typeof dateValue === 'number') {
        // 判断是否为秒级时间戳（小于10000000000），如果是则转换为毫秒
        let timestamp = dateValue
        if (timestamp < 10000000000) {
          timestamp = timestamp * 1000
        }
        
        const date = new Date(timestamp)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
      
      // 如果是字符串格式的日期
      if (typeof dateValue === 'string') {
        const date = new Date(dateValue)
        if (isNaN(date.getTime())) {
          // 如果无法解析，返回原值
          return dateValue
        }
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      }
      
      return 'Invalid Date'
    },
    // 获取摘要状态样式类
    

    getStatusText(status) {
      switch (status) {
        case 0: return 'Submitted'
        case 1: return 'Accepted'
        case 2: return 'Rejected'
        default: return 'Unknown Status'
      }
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 0: return 'badge-warning'  // 待审核 - 黄色
        case 1: return 'badge-success'  // 已通过 - 绿色
        case 2: return 'badge-danger'   // 未通过 - 红色
        default: return 'badge-secondary'
      }
    },

    getAuthorsList(authorsData) {
      if (!authorsData) return []
      
      // 如果是数组格式（authors_new）
      if (Array.isArray(authorsData)) {
        return authorsData.map(author => {
          const title = author.title ? `${author.title}. ` : ''
          return `${title}${author.first_name || ''} ${author.last_name || ''} <${author.email || ''}> (${author.job_title || ''}, ${author.country || ''})`
        }).filter(author => author.trim())
      }
      
      // 如果是字符串格式（兼容旧格式）
      if (typeof authorsData === 'string') {
        return authorsData.split(',').filter(author => author.trim())
      }
      
      return []
    },

    goBack() {
      this.$router.go(-1)
    },

    editAbstract() {
      // 跳转到编辑页面或显示编辑模态框
      alert('Edit functionality will be implemented here')
    },

    deleteAbstract() {
      if (confirm(`Are you sure you want to delete the abstract "${this.abstract.title}"?`)) {
        // 执行删除操作
        alert('Delete functionality will be implemented here')
        this.goBack()
      }
    },

    downloadAbstract() {
      // 下载摘要PDF
      alert('Download functionality will be implemented here')
    }
  }
}
</script>

<style scoped>
.abstract-details-container {
  padding: 20px 0;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.abstract-details-card {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  border: none;
  border-radius: 8px;
}

.card-header {
  background-color: #3498DB;
  color: white;
  border-radius: 8px 8px 0 0 !important;
  padding: 20px;
}

.card-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
}

.card-body {
  padding: 30px;
}

.section {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 20px;
}

.section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.section-title {
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #3498DB;
}

.info-item {
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
}

.info-label {
  font-weight: 600;
  color: #495057;
  min-width: 150px;
  margin-right: 15px;
}

.info-value {
  color: #212529;
  flex: 1;
}

.title-text {
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.4;
}

.abstract-content {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 6px;
  border-left: 4px solid #3498DB;
}

.abstract-content p {
  margin: 0;
  line-height: 1.6;
  color: #495057;
}

.authors-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.author-item {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-name {
  font-size: 14px;
  color: #495057;
  line-height: 1.4;
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.action-buttons .btn {
  min-width: 120px;
}

.badge {
  font-size: 0.8rem;
  padding: 6px 12px;
}

@media (max-width: 768px) {
  .card-body {
    padding: 20px;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .info-label {
    min-width: auto;
    margin-right: 0;
    margin-bottom: 5px;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .action-buttons .btn {
    width: 100%;
  }
}
</style> 